import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  wizardDistributionPartnerContactExclusionApi,
  wizardDistributionPartnerContactExclusionKeys,
} from '@client/api/waterlily';

export function useWizardDistributionPartnerContactExclusions() {
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: wizardDistributionPartnerContactExclusionKeys.list(),
    queryFn: () => wizardDistributionPartnerContactExclusionApi.list(),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: wizardDistributionPartnerContactExclusionKeys.list(),
    });

  const addMutation = useMutation({
    mutationFn: ({ email, note }: { email: string; note: string | null }) =>
      wizardDistributionPartnerContactExclusionApi.add(email, note),
    onSuccess: result => {
      const failed = result.failedConnectionIds.length;
      if (failed > 0) {
        // Surface partial failures instead of a false "all done".
        toast.error(
          `Excluded — ${result.revokedConnections} connection(s) revoked, ${failed} failed`,
          { description: 'Some connections could not be revoked. Please retry.' },
        );
      } else if (result.revokedConnections > 0) {
        toast.success(`Excluded — ${result.revokedConnections} connection(s) revoked`);
      } else {
        toast.success('Added to exclude list');
      }
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to add to exclude list', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => wizardDistributionPartnerContactExclusionApi.remove(id),
    onSuccess: () => {
      toast.success('Removed from exclude list');
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error('Failed to remove from exclude list', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    },
  });

  return {
    entries: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    addEntry: addMutation.mutate,
    isAdding: addMutation.isPending,
    removeEntry: removeMutation.mutate,
    isRemoving: removeMutation.isPending,
  };
}
